<template>
    <bill-shell title="ZESA Electricity Tokens">
        <section id="more" class="py-5 bg-white">
            <div class="container text-center">
                <img style="height: 56px" src="/img/zetdc-logo.webp" alt="ZETDC Logo">
                <h1 class="text-9 font-weight-bold mb-4 mt-2">Prepaid Meters: A Guide to Your Zesa Token on Magetsi.co.zw</h1>
                <p class="text-3">
                    Welcome to your one-stop shop for managing your electricity needs in Zimbabwe! Here at Magetsi, we understand the importance of having a reliable and convenient way to purchase and manage your Zesa token. This comprehensive guide will equip you with all the information you need to navigate the world of Zesa tokens, from understanding their purpose to purchasing them effortlessly on our secure platform.
                </p>
            </div>
        </section>

        <section class="pb-5 bg-white">
            <div class="container">
                <h3 class="text-7 font-weight-bold mb-5 text-center">What is A Zesa Token?</h3>
                <div class="row">
                    <div class="col-12">
                        <div class="text-3 text-center">
                            <p>
                                Zesa, also known as the Zimbabwe Electricity Transmission and Distribution Company (ZETDC), is the primary provider of electricity in Zimbabwe. They offer a pre-paid metering system where consumers purchase tokens, which are essentially electronic vouchers, to top up one’s electricity meter. The Zesa Token contains a 20-digit number that converts to the number of units you will get.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="pb-5 bg-white">
            <div class="container">
                <h3 class="text-7 font-weight-bold mb-5 text-center">Zimbabwe’s Introduction To Prepaid Meters: Why Use Zesa Token System?</h3>
                <div class="row">
                    <div class="col-12">
                        <div class="text-3 text-center">
                            <p>
                                There are several advantages to using Zesa tokens:
                            </p>
                            <ul class="text-left">
                                <li><strong>Budgeting:</strong> You gain greater control over your spending by pre-paying for your electricity. You can top up your meter as needed, preventing unexpected high bills at the end of the month.</li>
                                <li><strong>Convenience:</strong> Gone are the days of long queues and inconvenient payment schedules. With a Zesa token, you can purchase electricity anytime, anywhere, directly through Magetsi's user-friendly online platform.</li>
                                <li><strong>Improved Cash Flow:</strong> For businesses, having a Zesa token eliminates the need for hefty deposits or monthly bills. You only pay for the electricity you consume, improving cash flow management.</li>
                                <li><strong>Reduced Risk of Disconnection:</strong> Running out of credit on your pre-paid meter can lead to power disconnection. By purchasing your Zesa token regularly, you ensure an uninterrupted electricity supply for your needs.</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="pb-5 bg-white">
            <div class="container">
                <h3 class="text-7 font-weight-bold mb-5 text-center">How to Buy Your Zesa Tokens Online on Magetsi</h3>
                <div class="row">
                    <div class="col-12">
                        <div class="text-3 text-center">
                            <p>
                                Magetsi makes buying Zesa tokens a breeze. Here's a step-by-step guide:
                            </p>
                            <ol class="text-left">
                                <li><strong>Visit Magetsi.co.zw:</strong> Head over to our website and navigate to the "Buy Zesa Token" section.</li>
                                <li><strong>Enter Your Meter Number:</strong> Accurately input your unique Zesa meter number. This ensures the token is credited to the correct account.</li>
                                <li><strong>Select the Desired Amount:</strong> Choose the amount of electricity you wish to purchase. The minimum amount changes depending on the <router-link to="/tariffs"><strong>current Zesa tariffs</strong></router-link>.</li>
                                <li><strong>Choose Your Payment Method:</strong> Magetsi provides several secure payment options to cater to your preferences. You can choose from EcoCash, VISA, or Mastercard.</li>
                                <li><strong>Complete the Transaction:</strong> Follow the on-screen instructions to finalize your payment. Once successful, you'll receive a confirmation message and your Zesa token.</li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="pb-5 bg-white">
            <div class="container">
                <h3 class="text-7 font-weight-bold mb-5 text-center">Using EcoCash to Buy Your Zesa Token on Magetsi</h3>
                <div class="row">
                    <div class="col-12">
                        <div class="text-3 text-center">
                            <p>
                                EcoCash is a popular mobile money transfer service widely used in Zimbabwe. Here's how to utilize EcoCash for your Zesa token purchase on Magetsi:
                            </p>
                            <ol class="text-left">
                                <li>Follow Steps 1-3 from the previous section.</li>
                                <li>Select EcoCash as your payment method. You'll be presented with a unique reference number.</li>
                                <li>You will receive a prompt message on your mobile.</li>
                                <li>Enter your EcoCash PIN. Once confirmed, the payment will be processed.</li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="pb-5 bg-white">
            <div class="container">
                <h3 class="text-7 font-weight-bold mb-5 text-center">Viewing Your Zesa Token</h3>
                <div class="row">
                    <div class="col-12">
                        <div class="text-3 text-center">
                            <p>
                                There are two ways to view your Zesa token after a successful purchase on Magetsi:
                            </p>
                            <ul class="text-left">
                                <li><strong>Text Message:</strong> Your Zesa token will be sent to your phone soon after your transaction is completed.</li>
                                <li><strong>WhatsApp Message:</strong> When you purchase your Zesa token online using our WhatsApp platform, you will receive your token via WhatsApp.</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="pb-5 bg-white">
            <div class="container">
                <h3 class="text-7 font-weight-bold mb-5 text-center">Retrieving Lost Zesa Token</h3>
                <div class="row">
                    <div class="col-12">
                        <div class="text-3 text-center">
                            <p>
                                If you happen to misplace your Zesa token, fret not! You can retrieve it using the following methods:
                            </p>
                            <ul class="text-left">
                                <li><strong>Magetsi Platform:</strong> Contact Magetsi customer support if you purchased the token on our platform.</li>
                                <li><strong>Magetsi Website:</strong> Use our <router-link to="/zesa/view-token"><strong>ZESA View Token</strong></router-link> tool to instantly retrieve your previous Zesa tokens by entering your meter number.</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="pb-5 bg-white">
            <div class="container">
                <h3 class="text-7 font-weight-bold mb-5 text-center">Zesa Token Prices</h3>
                <div class="row">
                    <div class="col-12">
                        <div class="text-3 text-center">
                            <p>
                                Zesa token prices change on a regular basis. The best way to get accurate results is through our <router-link to="/calculators/zesa-calculator"><strong>Magetsi Zesa Calculator</strong></router-link>, a system that allows you to calculate the number of units you can get after your purchase. Alternatively, you can use the <router-link to="/zesa-tariffs"><strong>updated Zesa Tariffs</strong></router-link>.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </bill-shell>
</template>

<script>
import Carousel from "../../custom/carousel.vue";
import BillShell from "../bill-shell.vue";
import TariffsTable from "../../custom/tariffs-table.vue";

export default {
    name: "dashboard",
    components: { BillShell, TariffsTable, WelcomeInfo: () => import("../../shared/welcome-info.vue") },

    mounted() {
        const carousel = () => setTimeout(() => {
            if (this.car + 1 > 5) this.car = 1
            else this.car++

            carousel()
        }, 25000)

        carousel()
    },
    metaInfo() {
        return {
            title: "Buy ZESA Tokens",
        }
    }
}
</script>
